import {
  BssdkEventName,
  LanguageEventName,
  MONITORING_EVENT
} from './constants';
import {
  captureMonitoringException,
  reportMonitoringEvent
} from './client';
import { getDurationMs, normalizeCode, normalizeErrorType } from './sanitize';

export type TicketEmailApiName = 'email_submit' | 'faq_list' | 'email_guide' | 'logistics_create';
export type TicketEmailApiResult =
  | 'success'
  | 'biz_error'
  | 'network_error'
  | 'unexpected_error'
  | 'validation_error';
export type TicketEmailPage =
  | 'contact_us'
  | 'logistics_modification'
  | 'logistics_modification_new'
  | 'unknown';

type ApiResultInput = {
  apiName: TicketEmailApiName;
  result: TicketEmailApiResult;
  startedAt: number;
  code?: unknown;
  stage?: string;
  fallback?: 'local';
  priority?: 'low';
  page?: TicketEmailPage;
};

export type ApiTimingOutcome =
  | 'success'
  | 'biz_error'
  | 'http_error'
  | 'timeout'
  | 'network_error'
  | 'cancelled'
  | 'unknown_error';

type ApiTimingV2Input = {
  apiUrl: string;
  apiDomain: string;
  code?: unknown;
  method?: string;
  outcome: ApiTimingOutcome;
  timingSource: 'i18n_fetch_meta' | 'client_fallback';
  hasResourceTiming: boolean;
  logId?: string | number;
  metrics: Record<string, number>;
  samplingApplied?: boolean;
};

const API_TIMING_SUCCESS_SAMPLE_RATE = 0.1;

export const shouldReportApiTimingV2 = (outcome: ApiTimingOutcome): boolean =>
  outcome !== 'success' || Math.random() < API_TIMING_SUCCESS_SAMPLE_RATE;

type DependencyResultInput = {
  dependency: 'starling' | 'email_language_tcc';
  stage: 'load' | 'fetch' | 'parse';
  result: 'success' | 'network_error' | 'unexpected_error';
  startedAt: number;
  error?: unknown;
  locale?: string;
  projectId?: number;
};

export const getTicketEmailPage = (pathname?: string): TicketEmailPage => {
  const currentPathname =
    pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);

  if (currentPathname === '/contact-us/logistics-modification') {
    return 'logistics_modification';
  }
  if (currentPathname === '/contact-us/logistics-modification-new') {
    return 'logistics_modification_new';
  }
  if (currentPathname === '/contact-us') {
    return 'contact_us';
  }
  return 'unknown';
};

export const reportApiResult = ({
  apiName,
  result,
  startedAt,
  code,
  stage,
  fallback,
  priority,
  page = getTicketEmailPage()
}: ApiResultInput): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.apiResult,
    {
      api_name: apiName,
      result,
      code: normalizeCode(code),
      stage,
      fallback,
      priority,
      page
    },
    {
      duration_ms: getDurationMs(startedAt)
    }
  );
};

export const reportApiTimingV2 = ({
  apiUrl,
  apiDomain,
  code,
  method,
  outcome,
  timingSource,
  hasResourceTiming,
  logId,
  metrics,
  samplingApplied = false
}: ApiTimingV2Input): void => {
  const sampleRate = outcome === 'success' ? API_TIMING_SUCCESS_SAMPLE_RATE : 1;
  if (!samplingApplied && !shouldReportApiTimingV2(outcome)) {
    return;
  }

  reportMonitoringEvent(
    MONITORING_EVENT.apiTimingV2,
    {
      api_url: apiUrl,
      api_domain: apiDomain,
      api_code: normalizeCode(code),
      method,
      log_id: logId === undefined ? '' : String(logId),
      outcome,
      transport: 'fetch',
      timing_schema: 'v2',
      timing_source: timingSource,
      has_resource_timing: hasResourceTiming ? 1 : 0,
      has_detailed_resource_timing: Number.isFinite(metrics.request_to_ttfb_ms) ? 1 : 0,
      has_server_timing: Number.isFinite(metrics.server_total_ms) ? 1 : 0,
      sample_rate: sampleRate
    },
    metrics
  );
};

export const reportDependencyResult = ({
  dependency,
  stage,
  result,
  startedAt,
  error,
  locale,
  projectId
}: DependencyResultInput): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.dependencyResult,
    {
      dependency,
      stage,
      result,
      error_type: error === undefined ? undefined : normalizeErrorType(error),
      locale,
      project_id: projectId === undefined ? undefined : String(projectId)
    },
    {
      duration_ms: getDurationMs(startedAt)
    }
  );
};

export const reportBssdkEvent = (
  eventName: BssdkEventName,
  payload: Record<string, unknown> = {}
): void => {
  reportMonitoringEvent(eventName, {
    region: payload.region,
    is_boe: payload.is_boe,
    captcha_initialized: payload.captcha_initialized,
    captcha_status: payload.captcha_status,
    idc_name: payload.idc_name,
    error_type: payload.error === undefined ? undefined : normalizeErrorType(payload.error)
  });
};

export const reportLanguageEvent = (
  eventName: LanguageEventName,
  payload: { error?: unknown; locale?: string } = {}
): void => {
  reportMonitoringEvent(eventName, {
    error_type: payload.error === undefined ? undefined : normalizeErrorType(payload.error),
    locale: payload.locale
  });
};

export const reportRenderDegraded = ({
  error,
  hasRoot,
  rootChildCount,
  rootTextLength,
  page = getTicketEmailPage()
}: {
  error: unknown;
  hasRoot: boolean;
  rootChildCount: number;
  rootTextLength: number;
  page?: TicketEmailPage;
}): void => {
  reportMonitoringEvent(
    MONITORING_EVENT.blankScreenErrorBoundary,
    {
      error_type: normalizeErrorType(error),
      has_root: hasRoot,
      page
    },
    {
      root_child_count: rootChildCount,
      root_text_length: rootTextLength
    }
  );
};

export const captureBusinessException = (
  error: unknown,
  scene: 'email_submit' | 'error_boundary',
  page = getTicketEmailPage()
): void => {
  captureMonitoringException(error, {
    stage: scene,
    page
  });
};
